/*
 * drawerEngine.js — deterministic templating engine for the Longevity marker drawers.
 *
 * Pure, side-effect-free functions. Authored strictly to KALPA_DRAWER_COPY_SPEC.md
 * (sha256 a8d5584…de7511). Numbers come ONLY from the user's stored data — this module
 * never generates a clinical figure. Written in CommonJS so it is BOTH unit-testable in
 * plain Node AND importable by the CRA frontend (webpack synthesises named exports from
 * module.exports).
 *
 * Provides:
 *   - computeTrend(readings, markerKey)      §1 Reference-Change-Value trend phrase
 *   - ckdEpiEgfr(scr_mgdl, age, sex)          2021 race-free CKD-EPI creatinine eGFR
 *   - egfrStability(creatinineReadings, age, sex)
 *   - evalMarkerClauses(markerKey, ctx)       §2 conditional cross-marker/plausibility clauses
 */

// ── §1 TREND (Reference-Change-Value gated) ─────────────────────────────────
// Per-marker variability tier (spec §1). The % net-move threshold that must be
// EXCEEDED before we call a direction is the standard bidirectional Reference
// Change Value, RCV = 2.77 × CVI, using the CVI anchors the spec gives for each
// tier (tight = albumin 2.5%, medium = glucose 4.5%, loose = CRP 27%). Derived
// from the spec's own numbers via the textbook RCV formula — no invented data.
const MARKER_TIER = {
  albumin: 'tight', creatinine: 'tight', mcv: 'tight',
  glucose: 'medium', lymphocyte: 'medium', 'alkaline phosphatase': 'medium',
  crp: 'loose', rdw: 'loose', wbc: 'loose',
};
const TIER_CVI = { tight: 2.5, medium: 4.5, loose: 27 };           // spec §1 anchors
const RCV_PCT = {
  tight: +(2.77 * TIER_CVI.tight).toFixed(1),    // 6.9
  medium: +(2.77 * TIER_CVI.medium).toFixed(1),  // 12.5
  loose: +(2.77 * TIER_CVI.loose).toFixed(1),    // 74.8
};

// Normalise a unit string for equality: lowercase, strip spaces, treat IU/L ≡ U/L.
function _normUnit(u) {
  return String(u || '').toLowerCase().trim().replace(/\s+/g, '').replace(/^iu\//, 'u/');
}

// readings: [{date, value, unit?}] (any order). Returns { n, direction, phrase, pctChange, sinceDate }.
// Guards against MIXED-UNIT history (e.g. albumin recorded g/L in one draw and g/dL in another —
// a 10× scale jump that first-vs-last would misread as a false, alarming "down 91%"). We compare
// only readings in the SAME unit as the latest reading (units missing → kept), so the trend is
// always like-for-like.
function computeTrend(readings, markerKey, fmtDate) {
  const _fd = (typeof fmtDate === 'function') ? fmtDate : (d) => d;  // S5 — site-wide DD MMM YYYY
  const all = (readings || [])
    .filter(r => r && r.value != null && !isNaN(Number(r.value)))
    .map(r => ({ date: String(r.date || ''), value: Number(r.value), unit: _normUnit(r.unit) }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const latestUnit = all.length ? all[all.length - 1].unit : '';
  const clean = latestUnit
    ? all.filter(r => r.unit === latestUnit || r.unit === '')
    : all;
  const n = clean.length;
  if (n < 3) {
    return { n, direction: null, phrase: 'No trend yet — needs at least 3 readings.', pctChange: null, sinceDate: null };
  }
  const first = clean[0].value, last = clean[n - 1].value;
  const pct = first !== 0 ? ((last - first) / Math.abs(first)) * 100 : 0;
  const tier = MARKER_TIER[markerKey] || 'medium';
  const thr = RCV_PCT[tier];
  let direction, phrase;
  if (pct > thr) {
    direction = 'rising';
    phrase = `Your last ${n} readings are gently rising.`;
  } else if (pct < -thr) {
    direction = 'falling';
    phrase = `Your last ${n} readings are clearly down ${Math.abs(pct).toFixed(0)}% since ${_fd(clean[0].date)}.`;
  } else {
    direction = 'stable';
    phrase = `Your last ${n} readings are broadly stable.`;
  }
  return { n, direction, phrase, pctChange: +pct.toFixed(1), sinceDate: clean[0].date };
}

// ── CKD-EPI 2021 (race-free) creatinine eGFR ────────────────────────────────
// Inker et al., NEJM 2021. eGFR mL/min/1.73m². scr in mg/dL. Compute-and-use only;
// any display must be labelled "estimated (CKD-EPI)". Never stored as a lab value.
function ckdEpiEgfr(scr, age, sex) {
  const s = Number(scr), a = Number(age);
  if (!(s > 0) || !(a > 0)) return null;
  const female = String(sex || '').toLowerCase().startsWith('f');
  const kappa = female ? 0.7 : 0.9;
  const alpha = female ? -0.241 : -0.302;
  const r = s / kappa;
  const egfr = 142
    * Math.pow(Math.min(r, 1), alpha)
    * Math.pow(Math.max(r, 1), -1.200)
    * Math.pow(0.9938, a)
    * (female ? 1.012 : 1);
  return Math.round(egfr * 10) / 10;
}

// Assess eGFR stability across creatinine readings. Age-at-draw is approximated by the
// current age (the 0.9938^age factor moves eGFR <1%/yr — negligible for a stability check).
function egfrStability(creatinineReadings, age, sex) {
  const vals = (creatinineReadings || [])
    .filter(r => r && r.value != null && !isNaN(Number(r.value)))
    .map(r => ({ date: String(r.date || ''), value: Number(r.value) }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  if (!vals.length) return { egfr: null, stable: false, falling: false, latest: null };
  const egfrs = vals.map(v => ckdEpiEgfr(v.value, age, sex)).filter(x => x != null);
  if (!egfrs.length) return { egfr: null, stable: false, falling: false, latest: null };
  const latest = egfrs[egfrs.length - 1];
  const earliest = egfrs[0];
  const minE = Math.min(...egfrs), maxE = Math.max(...egfrs);
  // Red-flag ONLY when kidney filtration is actually below the normal floor (eGFR < 60).
  // A large swing that stays comfortably normal (e.g. a low early creatinine giving eGFR ~140
  // settling to a normal ~88) is ordinary variation, NOT decline — it must NOT trigger the
  // "review with your doctor" clause. eGFR ≥ 60 = normal filtration ⇒ stable/reassuring.
  const falling = latest < 60;
  const stable = latest >= 60;
  return { egfr: latest, latest, earliest, stable, falling, spread: +(maxE - minE).toFixed(1) };
}

// ── §2 CROSS-MARKER + PLAUSIBILITY CLAUSES ──────────────────────────────────
// ctx: {
//   status: 'low'|'low_borderline'|'normal'|'high_borderline'|'high',
//   value: number, trend: {direction},
//   related: (name)=> {value,status,low,high}|null   // full unfiltered panel map lookup
//   medsHas: (pattern)=> bool,                        // active medicine name-match
//   fact: (key)=> {value,date}|null,                  // health-context ledger fact
//   egfr: {egfr, stable, falling},                    // from egfrStability (creatinine only)
// }
// Returns { clauses: [string], doctor: bool }. Fire ONLY when the user's data triggers.
const HIGH = new Set(['high', 'high_borderline']);
const LOW = new Set(['low', 'low_borderline']);

function evalMarkerClauses(markerKey, ctx) {
  const c = ctx || {};
  const status = c.status;
  const isHigh = HIGH.has(status);
  const isLow = LOW.has(status);
  const rising = c.trend && c.trend.direction === 'rising';
  const related = c.related || (() => null);
  const medsHas = c.medsHas || (() => false);
  const fact = c.fact || (() => null);
  const rHigh = (name) => { const m = related(name); return !!(m && HIGH.has(m.status)); };
  const rLow = (name) => { const m = related(name); return !!(m && LOW.has(m.status)); };
  // S6 — name a related marker's ACTUAL status ("in range" / "high" / "low"), only when the
  // marker is present in this user's panel. `named` returns "label (status)" or null.
  const sw = (m) => m ? (HIGH.has(m.status) ? 'high' : LOW.has(m.status) ? 'low' : 'in range') : null;
  const named = (queryNames, label) => {
    for (const q of [].concat(queryNames)) { const m = related(q); if (m && m.value != null) return `${label} (${sw(m)})`; }
    return null;
  };
  const clauses = [];
  let doctor = false;

  switch (markerKey) {
    case 'creatinine': {
      const eg = c.egfr || {};
      if (eg.falling) {
        clauses.push('Your estimated kidney filtration (eGFR, CKD-EPI) has drifted down — worth reviewing with your doctor rather than assuming it is muscle.');
        doctor = true;
      } else if (medsHas('creatine') && eg.stable) {
        clauses.push(`You take creatine and your estimated eGFR (CKD-EPI) is stable at ~${Math.round(eg.egfr)} mL/min — a higher creatinine here most likely reflects muscle and supplementation, not kidney function.`);
      }
      break;
    }
    case 'glucose': {
      const hba1c = related('hba1c');
      // The unit comes from the marker, never hard-coded: HbA1c renders as % in the US
      // convention and mmol/mol (IFCC) under NHS convention, so a literal '%' here would
      // print "37%" on a UK instance. Falls back to '%' only when no unit is supplied.
      const a1cUnit = (hba1c && hba1c.unit) ? (/^%$/.test(String(hba1c.unit)) ? '%' : ` ${hba1c.unit}`) : '%';
      const a1cTxt = hba1c && hba1c.value != null ? `your HbA1c (a 90-day average) is ${hba1c.value}${a1cUnit} — the steadier read` : 'your HbA1c, a 90-day average, is the steadier read';
      if (medsHas('tirzepatide') || medsHas('mounjaro') || medsHas('semaglutide') || medsHas('ozempic') || medsHas('wegovy') || medsHas('glp')) {
        clauses.push(`${a1cTxt.charAt(0).toUpperCase() + a1cTxt.slice(1)}, and your GLP-1 medication supports keeping it there.`);
      } else if (isHigh || rising) {
        clauses.push(`For context, ${a1cTxt}.`);
      }
      break;
    }
    case 'crp': {
      if (c.value != null && Number(c.value) >= 10) {
        clauses.push('A CRP of 10 mg/L or more usually points to an acute cause — worth checking with your doctor and repeating once you are well.');
        doctor = true;
      } else if (isHigh) {
        const inf = fact('recent_infection') || fact('infection');
        if (inf) clauses.push(`You logged an infection${inf.date ? ' on ' + inf.date : ''}; CRP can stay raised for 1–2 weeks after — retest when fully well.`);
        if (rHigh('wbc') || rHigh('white blood')) clauses.push('Your white-cell count is also up, consistent with a temporary immune response.');
      }
      break;
    }
    case 'lymphocyte': {
      if (isLow || (c.value != null && Number(c.value) < 20)) {
        const otherCyto = rLow('wbc') || rLow('white blood') || rLow('hemoglobin') || rLow('haemoglobin') || rLow('platelet');
        if (otherCyto) {
          const lows = [named(['wbc', 'white blood'], 'white-cell count'), named('hemoglobin', 'haemoglobin'), named('platelet', 'platelets')]
            .filter(x => x && /\((low|high)\)/.test(x));
          clauses.push(`A low lymphocyte share alongside ${lows.length ? 'your ' + lows.join(' and ') : 'other low counts'} is worth discussing with your doctor.`);
          doctor = true;
        } else if (rHigh('crp')) {
          clauses.push(`Your CRP is a little raised (inflammation), which can transiently lower your lymphocyte share — worth re-checking once you are fully well.`);
        } else {
          const parts = [named(['wbc', 'white blood'], 'white-cell count'), named('crp', 'CRP')].filter(Boolean);
          if (parts.length) {
            clauses.push(`We read this together with your ${parts.join(' and ')} — with ${parts.length > 1 ? 'those' : 'that'} in range, a slightly low share on its own is usually not concerning and often settles after an infection.`);
          } else {
            clauses.push('A slightly low share on its own is usually not concerning and often settles after an infection.');
          }
        }
      }
      break;
    }
    case 'mcv': {
      if (isHigh) {
        let t = 'Worth confirming your B12 and folate; alcohol also raises MCV.';
        if (rLow('vitamin b12') || rLow('b12')) t = 'Your B12 is low, which can raise MCV — worth addressing; alcohol also raises it.';
        else if (rLow('folate')) t = 'Your folate is low, which can raise MCV — worth addressing; alcohol also raises it.';
        clauses.push(t);
      }
      break;
    }
    case 'rdw': {
      if (isHigh || rising) {
        const lowNut = [named('iron', 'iron'), named('ferritin', 'ferritin'), named(['vitamin b12', 'b12'], 'B12'), named('folate', 'folate')]
          .filter(x => x && /\(low\)/.test(x));
        const crpHigh = rHigh('crp');
        if (lowNut.length || crpHigh) {
          const bits = [...lowNut]; if (crpHigh) bits.push(named('crp', 'CRP'));
          clauses.push(`This often reflects nutrient status or inflammation — your ${bits.join(' and ')} ${bits.length > 1 ? 'stand' : 'stands'} out here; worth addressing ${lowNut.length ? 'the deficiency' : 'the inflammation'} first.`);
        }
      }
      break;
    }
    case 'alkaline phosphatase': {
      if (isHigh) {
        const liver = [named(['gamma', 'ggt'], 'GGT'), named('alt', 'ALT')].filter(x => x && /\(high\)/.test(x));
        if (liver.length) {
          clauses.push(`Raised alongside your ${liver.join(' and ')} — a liver or bile pattern worth discussing with your doctor.`);
          doctor = true;
        } else if (rLow('vitamin d') || rLow('25-oh')) {
          const vd = named(['vitamin d', '25-oh'], 'vitamin D');
          clauses.push(`Your ${vd || 'vitamin D'} can raise ALP — repleting it may be the lever.`);
        }
        if (status === 'high') { doctor = true; }   // markedly high → doctor
      }
      break;
    }
    case 'albumin': {
      if (isHigh && (rHigh('hematocrit') || rHigh('haematocrit'))) {
        const hct = named(['hematocrit', 'haematocrit'], 'haematocrit');
        clauses.push(`Your ${hct || 'haematocrit'} is also raised — you may have been dehydrated at the draw. Hydrate and retest to confirm.`);
      }
      break;
    }
    case 'wbc': {
      if (isHigh) {
        const inf = fact('recent_infection') || fact('infection');
        if (inf) clauses.push(`This can rise transiently after infection or stress${inf.date ? ' (you logged one on ' + inf.date + ')' : ''} — retest when well.`);
        else clauses.push('This can rise transiently after infection or stress — retest when well.');
      }
      break;
    }
    default:
      break;
  }
  return { clauses, doctor };
}

module.exports = {
  MARKER_TIER, TIER_CVI, RCV_PCT,
  computeTrend, ckdEpiEgfr, egfrStability, evalMarkerClauses,
};
